<template>
  <div>
    <v-dialog
      @keydown.esc="isOpen=false"
      persistent
      v-model="isOpen"
      width="416">
      <v-card>
        <div class="d-flex justify-end">
          <v-btn
            fab
            color="transparent"
            small
            depressed
            @click="closeDialog()"
            class="mr-1 mt-1"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
        <v-card-title class="pt-0 px-8 title">
          {{ $t('expenses.incomeCategories.conceptDialog.title') }}
        </v-card-title>
        <v-card-text class="px-8 pb-8">
          <div class="d-flex justify-end mb-2">
            <Button size="x-small" @click="openEditDialog()">
              <v-icon>mdi-plus</v-icon>
              <span>{{ $t('expenses.incomeCategories.conceptDialog.buttonCreate') }}</span>
            </Button>
          </div>
          <div>
            <v-data-table
              v-if="table.incomeConcepts.data.length"
              :loading-text="table.incomeConcepts.loadingText"
              :loading="table.incomeConcepts.loading"
              :headers="table.incomeConcepts.headers"
              :items="table.incomeConcepts.data"
              :options="table.incomeConcepts.options"
              hide-default-footer
              sort-by="name"
              class="table"
            >
              <template v-slot:[`item.type`]="{ item }">
                <div>{{ getTypeName(item.type) }}</div>
              </template>
              <template v-slot:[`item.name`]="{ item }">
                <div>{{ item.name }}</div>
              </template>
              <template v-slot:[`item.action`]="{ item }">
                <div class="d-flex justify-end">
                  <v-btn
                    fab
                    color="primary"
                    x-small
                    depressed
                    @click="openEditDialog({
                      _id: item._id,
                      name: item.name,
                      type: item.type
                    })"
                  >
                    <v-icon>mdi-pencil</v-icon>
                  </v-btn>
                </div>
              </template>
            </v-data-table>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog
      @keydown.esc="editDialogOpen=false"
      persistent
      v-model="editDialogOpen"
      width="416">
      <v-card>
        <div class="d-flex justify-end">
          <v-btn
            fab
            color="transparent"
            small
            depressed
            @click="closeEditDialog()"
            class="mr-1 mt-1"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
        <v-card-title class="pt-0 px-8 pb-6 title">
          {{ this.form._id ? $t('expenses.incomeCategories.editConceptDialog.editTitle') : $t('expenses.incomeCategories.editConceptDialog.createTitle') }}
        </v-card-title>
        <v-card-text class="px-8">
          <v-form v-model="isValid" ref="form">
            <v-row>
              <v-col class="py-0" cols="12">
                <div class="d-flex">
                  <v-select
                    rounded
                    outlined
                    dense
                    v-model="form.type"
                    :rules="rules.required"
                    item-text="name"
                    item-value="_id"
                    :items="types"
                    :label="$t('expenses.incomeCategories.editConceptDialog.type')"
                  />
                  <div class="pl-3">
                    <v-tooltip top v-if="expensesPermissions['create-income-type']">
                      <template v-slot:activator="{ on }">
                        <v-btn
                          v-on="on"
                          fab
                          color="primary"
                          x-small
                          depressed
                          @click="openAddOutgoingTypeDialog()"
                        >
                          <v-icon>mdi-pencil</v-icon>
                        </v-btn>
                      </template>
                      <span>Editar tipo de egreso</span>
                    </v-tooltip>
                  </div>
                </div>
              </v-col>
              <v-col class="py-0" cols="12">
                <EditOutlinedInput
                  v-model="form.name"
                  :rules="rules.required"
                  :label="$t('expenses.incomeCategories.editConceptDialog.name')"
                />
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions class="d-flex justify-space-between px-10 pb-8">
          <Button size="x-small" type="outlined" @click="closeEditDialog()">
            {{ $t('expenses.incomeCategories.editConceptDialog.buttonCancel') }}
          </Button>
          <Button size="x-small" @click="create()">
            {{ this.form._id ? $t('expenses.incomeCategories.editConceptDialog.buttonEdit') : $t('expenses.incomeCategories.editConceptDialog.buttonCreate') }}
          </Button>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <AddIncomeType
      ref="addType"
      @updated="loadTypes"
    />

    <v-overlay :value="overlay" z-index="99999">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>
  </div>
</template>

<script>
import AddIncomeType from '@/components/modals/AddIncomeType'
import EditOutlinedInput from '@/components/elements/EditOutlinedInput'
import Button from '@/components/elements/Button'

export default {
  components: {
    AddIncomeType,
    EditOutlinedInput,
    Button
  },
  data () {
    return {
      overlay: false,
      isOpen: false,
      editDialogOpen: false,
      isValid: false,
      form: {
        name: null,
        type: null
      },
      types: [],
      rules: {
        required: [v => !!v || `Este campo ${this.$i18n.t('messages.isRequired')}`]
      },
      table: {
        incomeConcepts: {
          loadingText: this.$i18n.t('expenses.incomeCategories.conceptDialog.table.loadingText'),
          loading: false,
          headers: [
            { text: this.$i18n.t('expenses.incomeCategories.conceptDialog.table.headers.type'), value: 'type' },
            { text: this.$i18n.t('expenses.incomeCategories.conceptDialog.table.headers.name'), value: 'name' },
            { text: '', value: 'action' }
          ],
          data: [],
          total: 0,
          options: {
            groupBy: [],
            groupDesc: [],
            itemsPerPage: 50,
            multiSort: false,
            mustSort: false,
            page: 1,
            sortBy: [''],
            sortDesc: []
          }
        }
      }
    }
  },
  computed: {
    expensesPermissions () {
      return this.$store.state.login.permissions['expenses'].components['income-category'].actions
    }
  },
  watch: {
    isOpen: async function (val) {
      if (val) {
        await this.loadTypes()
        await this.loadConcepts()
      }
    }
  },
  methods: {
    openDialog () {
      this.isOpen = true
    },
    openEditDialog (concept) {
      if (concept) {
        this.form = {
          ...concept
        }
      }
      this.editDialogOpen = true
    },
    closeDialog () {
      this.isOpen = false
      this.table.incomeConcepts.data = []
    },
    closeEditDialog () {
      this.editDialogOpen = false
      this.form = {
        name: null,
        type: null
      }
      this.$refs.form.resetValidation()
    },
    async create () {
      this.$refs.form.validate()
      if (this.isValid) {
        this.overlay = true
        try {
          const payload = {
            ...this.form,
            kind: 'income',
            restaurant: this.$store.state.currentRestaurant.data._id
          }
          if (this.form._id) {
            await this.$api.put(`/v1/cashflows/concepts/${this.form._id}`, payload)
          } else {
            await this.$api.post(`/v1/cashflows/concepts`, payload)
          }
          this.loadTypes()
          this.loadConcepts()
          this.$emit('updated')
          this.closeEditDialog()
        } catch (error) {
          this.$toasted.show(error.message, { duration: 3000 })
        }
        this.overlay = false
      }
    },
    async loadConcepts (params = null) {
      params = params || {
        kind: 'income',
        restaurant: this.$store.state.currentRestaurant.data._id
      }
      this.table.incomeConcepts.loading = true
      this.overlay = true
      try {
        const response = await this.$api.get(`/v1/cashflows/concepts`, params)
        this.table.incomeConcepts.data = response.data
      } catch (error) {
        this.$toasted.show(error.message, { duration: 3000 })
      }
      this.table.incomeConcepts.loading = false
      this.overlay = false
    },
    async loadTypes (params = null) {
      params = params || {
        kind: 'income',
        restaurant: this.$store.state.currentRestaurant.data._id
      }
      this.overlay = true
      try {
        const response = await this.$api.get(`/v1/cashflows/types`, params)
        this.types = response.data
      } catch (error) {
        this.$toasted.show(error.message, { duration: 3000 })
      }
      this.overlay = false
    },
    openAddOutgoingTypeDialog (type) {
      this.$refs.addType.openDialog(type)
    },
    getTypeName (typeId) {
      const foundType = this.types.find(type => typeId === type._id)
      return foundType ? foundType.name : ''
    }
  }
}
</script>

<style lang="scss" scoped>
  .title {
    font-weight: 500;
    font-size: 22px;
    color: #01D28E;
  }

  .table {
    overflow-y: auto;
    max-height: 65vh;
  }

  .table tr {
    border: 1px solid #ACABAB;
    border-radius: 7px;
  }

  .table td:first-child {
    border-left: 1px solid #ACABAB;
    border-top-left-radius: 7px;
    border-bottom-left-radius: 7px;
  }

  .table td {
    border-top: 1px solid #ACABAB;
    border-bottom: 1px solid #ACABAB;
  }

  .table td:last-child {
    border-right: 1px solid #ACABAB;
    border-top-right-radius: 7px;
    border-bottom-right-radius: 7px;
  }

  .table thead tr:last-child th {
    border-bottom: none !important;
  }
</style>
